import { z } from 'zod';

/** Non-negative integer in base-10, no sign, no leading zeros, no exponent. Arbitrary size. */
export const ATOMIC_AMOUNT_PATTERN = /^(0|[1-9][0-9]*)$/;

export const AtomicAmount = z
  .string()
  .regex(ATOMIC_AMOUNT_PATTERN, 'amountAtomic must be a non-negative base-10 integer string')
  .max(40, 'amountAtomic exceeds supported precision');
export type AtomicAmount = z.infer<typeof AtomicAmount>;

/** Positive integer carried as a decimal string (odds numerators etc.). */
export const PositiveIntString = z
  .string()
  .regex(/^[1-9][0-9]*$/, 'must be a positive base-10 integer string')
  .max(30);

/** ISO-8601 UTC wall-clock instant with millisecond precision, e.g. 2026-09-28T12:00:15.000Z. */
export const UtcInstant = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, 'must be UTC ISO-8601 with milliseconds and Z')
  .brand<'UtcInstant'>();
export type UtcInstant = z.infer<typeof UtcInstant>;

/**
 * Presentation timestamp inside a specific media source epoch. Not a wall-clock time:
 * a PTS is only comparable to other PTS values of the same sourceEpoch.
 */
export const MediaPts = z
  .object({
    sourceEpoch: z.string().min(1).max(128),
    ptsMicros: z.number().int().nonnegative(),
  })
  .brand<'MediaPts'>();
export type MediaPts = z.infer<typeof MediaPts>;

export const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/, 'must be lowercase hex sha-256');

const id = <B extends string>(_brand: B) => z.string().min(1).max(128).brand<B>();

export const GameRoundId = id('GameRoundId');
export type GameRoundId = z.infer<typeof GameRoundId>;
export const BetId = id('BetId');
export type BetId = z.infer<typeof BetId>;
/** Round identifier as defined by a partner wallet contract. Never interchangeable with GameRoundId. */
export const PartnerWalletRoundId = id('PartnerWalletRoundId');
export type PartnerWalletRoundId = z.infer<typeof PartnerWalletRoundId>;
export const CameraAliasId = id('CameraAliasId');
export type CameraAliasId = z.infer<typeof CameraAliasId>;
export const TrackId = id('TrackId');
export type TrackId = z.infer<typeof TrackId>;
export const CrossingEventId = id('CrossingEventId');
export type CrossingEventId = z.infer<typeof CrossingEventId>;
export const CommandId = id('CommandId');
export type CommandId = z.infer<typeof CommandId>;
export const WalletTransactionId = id('WalletTransactionId');
export type WalletTransactionId = z.infer<typeof WalletTransactionId>;
export const AggregatorId = id('AggregatorId');
export type AggregatorId = z.infer<typeof AggregatorId>;
export const OperatorId = id('OperatorId');
export type OperatorId = z.infer<typeof OperatorId>;
export const BrandId = id('BrandId');
export type BrandId = z.infer<typeof BrandId>;
export const ExternalPlayerId = id('ExternalPlayerId');
export type ExternalPlayerId = z.infer<typeof ExternalPlayerId>;
export const InternalPlayerId = id('InternalPlayerId');
export type InternalPlayerId = z.infer<typeof InternalPlayerId>;
export const SessionId = id('SessionId');
export type SessionId = z.infer<typeof SessionId>;
export const PromotionId = id('PromotionId');
export type PromotionId = z.infer<typeof PromotionId>;

/** Normalised coordinate in [0,1] relative to the intrinsic video frame. */
export const NormCoord = z.number().min(0).max(1);
export const NormPoint = z.object({ x: NormCoord, y: NormCoord });
export type NormPoint = z.infer<typeof NormPoint>;
